/**
 * Logger Tags Constants
 * Centralized logger tags for consistent logging across the application
 */

// Feature Tags
export const LOGGER_FEATURE = {
  ANALYTICS: 'analytics',
  PAYMENT: 'payment',
  FIREBASE: 'firebase',
  CHECKOUT: 'checkout',
  USER: 'user',
  AUTHENTICATION: 'authentication',
  USER_PROFILE: 'user-profile',
  APP_INIT: 'app-init',
  EXTENSION: 'extension',
  FEATURE_FLAGS: 'feature-flags',
  PREMIUM: 'premium',
  BRAZE: 'braze',
  WEB_HISTORY: 'web-history',
  MIXPANEL: 'mixpanel',
  FEEDBACK: 'feedback',
  INSIGHTS: 'insights',
  STATISTICS: 'statistics',
  EXTENSION_UNINSTALL: 'extension uninstall',
  APPSFLYER: 'appsflyer',
  GROUPS: 'groups',
} as const;

// Action Tags
export const LOGGER_ACTION = {
  // Payment & Stripe actions
  VALIDATE: 'validate',
  SUBMIT: 'submit',
  CREATE_PAYMENT_METHOD: 'create-payment-method',
  THREE_D_SECURE_NOTIFICATION: '3d-secure-notification',
  DISCLAIMER_CHECK: 'disclaimer-check',
  PROCESS_PAYMENT: 'process-payment',
  APPROVE_PAYMENT: 'approve-payment',
  POST_LOGIN_PREMIUM_CHECK: 'post-login-premium-check',
  INIT_SDK: 'init-sdk',
  LOAD_SDK: 'load-sdk',
  CREATE_CUSTOMER: 'create-customer',
  CREATE_SUBSCRIPTION: 'create-subscription',
  THREE_D_SUBSCRIPTION_UPDATE: '3d-subscription-update',
  THREE_D_PURCHASE_UPDATE: '3d-purchase-update',
  EXTENSION_SYNC: 'extension_sync',
  SET_GTM_DATA: 'set-gtm-data',

  // PayPal actions
  CANCEL: 'cancel',
  BUTTON_ERROR: 'button-error',
  CLEANUP: 'cleanup',
  CREATE_PURCHASE: 'create-purchase',
  PENDING_SUBSCRIPTION: 'pending-subscription',
  APPROVE_PURCHASE: 'approve-purchase',
  APPROVE_SUBSCRIPTION: 'approve-subscription',
  GET_ORDER_INFO: 'get-order-info',
  GET_SUBSCRIPTION_INFO: 'get-subscription-info',
  NOT_ELIGIBLE: 'not-eligible',

  // Firebase & Authentication actions
  EMAIL_LOGIN: 'email-login',
  REGISTER: 'register',
  LOGOUT: 'logout',
  GOOGLE_ONE_TAP: 'google-one-tap',
  GOOGLE_ONE_TAP_INIT: 'google-one-tap-init',
  ANONYMOUS_LOGIN: 'anonymous-login',
  GOOGLE_AUTH: 'google-auth',

  // User actions
  GET_PREMIUM_DETAILS: 'get-premium-details',
  MERGE_USERS_IN_BRAZE: 'merge-users-in-braze',
  UPDATE_FLAG_CONFIG: 'update-flag-config',
  UPDATE_GROUP_ITEMS: 'update-group-items',
  CREATE_GROUP: 'create-group',
  UPDATE_GROUP: 'update-group',
  DELETE_GROUP: 'delete-group',
  UPDATE: 'update',
  SAVE_USER_CONFIG: 'save-user-config',
  SYNC_FROM_REMOTE: 'sync-from-remote',
  CHECK_EMAIL_EXISTS: 'check-email-exists',
  SAVE_CUSTOMIZED_BLOCK_PAGE: 'save-customized-block-page',

  // Checkout actions
  STORE_CONSENT: 'store-consent',

  // Extension actions
  CONFIG_UPDATE: 'config-update',
  SEND_TOKEN: 'send-token',
  SEND_MESSAGE: 'send-message',
  GET_INFO: 'get-info',

  // Premium actions
  LOAD_PLANS: 'load-plans',

  // Generic actions
  INIT: 'init',
  TRACK: 'track',
  LOAD: 'load',
  RECEIVE: 'receive',

  // AppsFlyer actions
  SET_CUSTOMER_USER_ID: 'set-customer-user-id',

  // Web History actions
  POST_GOOGLE_TOKEN: 'post-google-token',

  // Feedback actions
  SEND_GOOGLE_FORM: 'send-google-form',

  // Statistics actions
  SEND_BLOCKED_EVENT: 'send-blocked-event',

  // Groups actions
  SEARCH_AUTOCOMPLETE: 'search-autocomplete',
  SAVE_RULE: 'save-rule',
  TURN_OFF_RULE: 'turn-off-rule',
  TURN_ON_RULE: 'turn-on-rule',
  DELETE_RULE: 'delete-rule',
} as const;

// Provider Tags
export const LOGGER_PROVIDER = {
  STRIPE: 'stripe',
  PAYPAL: 'paypal',
} as const;

// Other Tags
export const LOGGER_OTHER = {
  SECURITY_UNVERIFIED: 'unverified',
  CRITICAL: true,
} as const;

// Service Tags
export const LOGGER_SERVICE = {
  FEATURE_FLAG_API: 'feature-flag-api',
} as const;

// Logged when a consent variant resolves to a bucket set without clickstream,
// which is only reachable through a GrowthBook targeting mistake.
export const CONSENT_WRITE_INVALID =
  'consent variant cannot express a valid consent state';
